import React, { useState, useEffect } from "react";
import { ProjectService } from "../services/projectService";
import { formatDate } from "../utils/dateFormatting";
import { formatJobRevNumber } from '../utils/projectFormatting';
import { Modal, ModalBody, ModalFooter, Input, Dropdown, Button, NCBadge } from "./ui";

const OfficeAdminModal = ({
  project,
  clients,
  contacts,
  employees,
  isOpen,
  onClose,
  onSave,
  theme = 'dark',
}) => {
  const [formData, setFormData] = useState({
    modelPrice: "",
    poNumber: "",
    invoiceDate: "",
    initialAcceptance: "",
    paidDate: "",
    modelingStatus: "",
    billingStatus: "",
    agreementStatus: "",
    readyToInvoice: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (project) {
      setFormData({
        modelPrice: project.modelPrice || project.price || "",
        poNumber: project.poNumber || "",
        invoiceDate: project.invoiceDate
          ? formatDate(project.invoiceDate, "input")
          : "",
        initialAcceptance: project.initialAcceptance
          ? formatDate(project.initialAcceptance, "input")
          : "",
        paidDate: project.paidDate ? formatDate(project.paidDate, "input") : "",
        modelingStatus: project.modelingStatus || "",
        billingStatus: project.billingStatus || "",
        agreementStatus: project.agreementStatus || "",
        readyToInvoice: project.readyToInvoice || "",
      });
    }
  }, [project]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    const fieldsToUpdate = {
      model_price: formData.modelPrice ? parseFloat(formData.modelPrice) : null,
      po_number: formData.poNumber || null,
      invoiced_date: formData.invoiceDate || null,
      initial_acceptance: formData.initialAcceptance || null,
      paid_date: formData.paidDate || null,
      modeling_status: formData.modelingStatus || null,
      billing_status: formData.billingStatus || null,
      agreement_status: formData.agreementStatus || null,
      ready_to_invoice: formData.readyToInvoice || null,
    };

    try {
      setSaving(true);
      setError(null);

      for (const [field, value] of Object.entries(fieldsToUpdate)) {
        let currentFieldName;
        if (field === 'model_price') {
          currentFieldName = 'modelPrice';
        } else if (field === 'invoiced_date') {
          currentFieldName = 'invoiceDate';
        } else if (field === 'ready_to_invoice') {
          currentFieldName = 'readyToInvoice';
        } else if (field === 'po_number') {
          currentFieldName = 'poNumber';
        } else if (field === 'paid_date') {
          currentFieldName = 'paidDate';
        } else if (field === 'initial_acceptance') {
          currentFieldName = 'initialAcceptance';
        } else if (field === 'modeling_status') {
          currentFieldName = 'modelingStatus';
        } else if (field === 'billing_status') {
          currentFieldName = 'billingStatus';
        } else if (field === 'agreement_status') {
          currentFieldName = 'agreementStatus';
        } else {
          currentFieldName = field.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
        }

        if (value !== project[currentFieldName]) {
          console.log(`Updating field ${field} from "${project[currentFieldName]}" to "${value}"`);
          try {
            await ProjectService.updateProject(project.id, field, value);
          } catch (fieldError) {
            console.error(`Error updating field ${field}:`, fieldError);
            throw new Error(`Failed to update ${field}: ${fieldError.message}`);
          }
        }
      }

      if (onSave) {
        await onSave();
      }
      onClose();
    } catch (err) {
      console.error("Error saving project:", err);
      setError(`Failed to save changes: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!project) return null;

  return (
    <Modal trackAs="OfficeAdminModal" isOpen={isOpen} onClose={onClose} size="lg" theme={theme}>
      {/* Standard Project Modal Header */}
      <div
        style={{
          padding: 'var(--space-6)',
          borderBottom: '1px solid var(--gray-700)',
          background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
          borderTopLeftRadius: 'var(--radius-xl)',
          borderTopRightRadius: 'var(--radius-xl)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              <span
                style={{
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-400)',
                  flexShrink: 0,
                }}
              >
                {formatJobRevNumber(project.jobNumber, project.rev)}
              </span>
              <h1
                style={{
                  margin: 0,
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-50)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {project.projectName || project.name}
              </h1>
            </div>
            <p
              style={{
                margin: 0,
                marginTop: 'var(--space-2)',
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-400)',
              }}
            >
              Invoice & Status Details
            </p>
          </div>
        </div>
      </div>

      <ModalBody>
        {error && (
          <div style={{
            padding: 'var(--space-4)',
            marginBottom: 'var(--space-4)',
            background: 'var(--danger-bg, #dc26261a)',
            border: '1px solid var(--danger, #dc2626)',
            borderRadius: 'var(--radius-lg)',
            color: 'var(--danger, #dc2626)',
            fontSize: 'var(--text-sm)',
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
          {/* Project Information Section */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <h2 style={{
              margin: 0,
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--gray-200)',
              paddingBottom: 'var(--space-2)',
              borderBottom: '1px solid var(--gray-700)',
            }}>
              Project Information
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)'
                }}>
                  Client
                </label>
                <div style={{
                  padding: 'var(--space-3)',
                  background: 'var(--dark-elevated)',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-300)',
                }}>
                  {project.clientName || 'Not Set'}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <Input
                  label="Model Price (Client Invoice)"
                  type="number"
                  value={formData.modelPrice}
                  onChange={(e) => handleInputChange("modelPrice", e.target.value)}
                  placeholder="Enter model price"
                />
                {project.discountPercentage > 0 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-2) var(--space-3)',
                    background: 'rgba(168, 85, 247, 0.1)',
                    border: '1px solid rgba(168, 85, 247, 0.2)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--text-sm)',
                  }}>
                    <NCBadge
                      modelPrice={project.modelPrice}
                      tnRevenue={project.tnRevenue}
                      discountPercentage={project.discountPercentage}
                      size="sm"
                    />
                    <span style={{ color: 'var(--gray-400)' }}>
                      TN Receives: <strong style={{ color: '#c084fc' }}>
                        ${project.tnRevenue?.toLocaleString() || 'TBD'}
                      </strong>
                    </span>
                  </div>
                )}
              </div>

              <Input
                label="PO Number"
                type="text"
                value={formData.poNumber}
                onChange={(e) => handleInputChange("poNumber", e.target.value)}
                placeholder="Enter PO number"
              />

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)'
                }}>
                  Contact
                </label>
                <div style={{
                  padding: 'var(--space-3)',
                  background: 'var(--dark-elevated)',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-300)',
                }}>
                  {project.contactName || 'Not Set'}
                </div>
              </div>

              <Input
                label="Initial Acceptance"
                type="date"
                value={formData.initialAcceptance}
                onChange={(e) => handleInputChange("initialAcceptance", e.target.value)}
              />
            </div>
          </div>

          {/* Status Information Section */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <h2 style={{
              margin: 0,
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--gray-200)',
              paddingBottom: 'var(--space-2)',
              borderBottom: '1px solid var(--gray-700)',
            }}>
              Status Information
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
              <Dropdown
                label="Modeling Status"
                value={formData.modelingStatus}
                onChange={(value) => handleInputChange("modelingStatus", value)}
                options={[
                  { value: "", label: "Not Set" },
                  { value: "Startup", label: "Startup" },
                  { value: "Data Prep", label: "Data Prep" },
                  { value: "Ready to Model", label: "Ready to Model" },
                  { value: "Modeling", label: "Modeling" },
                  { value: "Double Check", label: "Double Check" },
                  { value: "Corrections", label: "Corrections" },
                  { value: "Review", label: "Review" },
                  { value: "Ready to Export", label: "Ready to Export" },
                  { value: "Utilities", label: "Utilities" },
                  { value: "Completed", label: "Completed" },
                ]}
              />

              <Dropdown
                label="Billing Status"
                value={formData.billingStatus}
                onChange={(value) => handleInputChange("billingStatus", value)}
                options={[
                  { value: "", label: "Not Set" },
                  { value: "Quoted", label: "Quoted" },
                  { value: "Invoiced", label: "Invoiced" },
                  { value: "Paid", label: "Paid" },
                  { value: "Not Paid", label: "Not Paid" },
                  { value: "Voided", label: "Voided" },
                ]}
              />

              <Dropdown
                label="Agreement Status"
                value={formData.agreementStatus}
                onChange={(value) => handleInputChange("agreementStatus", value)}
                options={[
                  { value: "", label: "Not Set" },
                  { value: "Ready", label: "Ready" },
                  { value: "Utility Ready", label: "Utility Ready" },
                  { value: "Sent", label: "Sent" },
                  { value: "Completed", label: "Completed" },
                  { value: "None needed", label: "None needed" },
                  { value: "Revision-none", label: "Revision-none" },
                  { value: "Voided", label: "Voided" },
                  { value: "Hold", label: "Hold" },
                  { value: "Not Signed", label: "Not Signed" },
                ]}
              />

              <Dropdown
                label="Ready to Invoice"
                value={formData.readyToInvoice}
                onChange={(value) => handleInputChange("readyToInvoice", value)}
                options={[
                  { value: "", label: "Not Set" },
                  { value: "Yes", label: "Yes" },
                  { value: "None Needed", label: "None Needed" },
                ]}
              />
            </div>
          </div>

          {/* Invoice Information Section */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <h2 style={{
              margin: 0,
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--gray-200)',
              paddingBottom: 'var(--space-2)',
              borderBottom: '1px solid var(--gray-700)',
            }}>
              Invoice Information
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
              <div></div>
              <Input
                label="Invoice Date"
                type="date"
                value={formData.invoiceDate}
                onChange={(e) => handleInputChange("invoiceDate", e.target.value)}
              />

              <div></div>
              <Input
                label="Paid Date"
                type="date"
                value={formData.paidDate}
                onChange={(e) => handleInputChange("paidDate", e.target.value)}
              />
            </div>
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: '100%',
          gap: 'var(--space-3)',
        }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} loading={saving}>
            Save Changes
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default OfficeAdminModal;
